import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { ChevronDownIcon, MagnifyingGlassIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { CheckIcon } from '@heroicons/react/24/solid'
import type { DirectoryUser } from '../../api/askEvansApi'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function normalizeAudienceEmail(raw: string) {
  return raw.trim().toLowerCase().replace(/,$/, '')
}

export function isAudienceEmail(raw: string) {
  return EMAIL_RE.test(normalizeAudienceEmail(raw))
}

function personLabel(person: { email: string; name?: string }) {
  const name = person.name?.trim()
  if (name && name.toLowerCase() !== person.email) return `${name} (${person.email})`
  return person.email
}

export function AudiencePicker({
  emails,
  directory,
  directoryStatus,
  draft,
  disabled,
  onDraft,
  onChange,
  onInvalid
}: {
  emails: string[]
  directory: DirectoryUser[]
  directoryStatus: 'loading' | 'ready' | 'error'
  draft: string
  disabled: boolean
  onDraft: (value: string) => void
  onChange: (emails: string[]) => void
  onInvalid: (message: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [adding, setAdding] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const addRef = useRef<HTMLInputElement>(null)
  const listId = useId()

  const byEmail = useMemo(() => {
    const map = new Map<string, DirectoryUser>()
    directory.forEach((person) => map.set(person.email, person))
    return map
  }, [directory])

  const options = useMemo(() => {
    const rows = new Map<string, DirectoryUser>()
    directory.forEach((person) => rows.set(person.email, person))
    emails.forEach((email) => {
      if (!rows.has(email)) rows.set(email, { email, name: '' })
    })
    const list: DirectoryUser[] = []
    rows.forEach((person) => list.push(person))
    return list.sort((a, b) => personLabel(a).localeCompare(personLabel(b), undefined, { sensitivity: 'base' }))
  }, [directory, emails])

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return options
    return options.filter((person) => personLabel(person).toLowerCase().includes(needle))
  }, [options, query])

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false)
        setQuery('')
        setAdding(false)
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [])

  useEffect(() => {
    if (!open) return
    const timer = window.setTimeout(() => (adding ? addRef.current : searchRef.current)?.focus(), 30)
    return () => window.clearTimeout(timer)
  }, [open, adding])

  function toggle(email: string) {
    onInvalid('')
    onChange(emails.includes(email) ? emails.filter((item) => item !== email) : [...emails, email])
  }

  function addDraft() {
    const email = normalizeAudienceEmail(draft)
    if (!email) return
    if (!isAudienceEmail(email)) {
      onInvalid('Enter a full email address, such as name@crce.com.')
      return
    }
    onInvalid('')
    if (!emails.includes(email)) onChange([...emails, email])
    onDraft('')
    setAdding(false)
  }

  return (
    <div className="ml-7 space-y-1.5" ref={rootRef}>
      <p className="text-[13px] font-medium text-blueprint-blue">Enabled for</p>
      <div className="relative">
        <div
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-label="Enabled for"
          className={`flex min-h-[44px] cursor-pointer flex-wrap items-center gap-1.5 rounded-lg border bg-white px-2 py-1.5 ${
            open ? 'border-blueprint-blue ring-1 ring-blueprint-blue' : 'border-[#E2E5EA]'
          }`}
          onClick={() => {
            if (!disabled) setOpen((value) => !value)
          }}
        >
          {emails.length === 0 ? (
            <span className="px-1 text-[13px] text-heritage-grey">Search and select people</span>
          ) : (
            emails.map((email) => (
              <span
                key={email}
                className="inline-flex max-w-full items-center gap-1 rounded-md border border-[rgba(0,39,63,0.10)] bg-[rgba(0,39,63,0.06)] px-2 py-0.5 text-[12px] font-medium text-blueprint-blue"
              >
                <span className="truncate">{personLabel(byEmail.get(email) || { email })}</span>
                <button
                  type="button"
                  aria-label={`Remove ${personLabel(byEmail.get(email) || { email })}`}
                  disabled={disabled}
                  className="rounded text-heritage-grey hover:text-hero-red"
                  onClick={(event) => {
                    event.stopPropagation()
                    toggle(email)
                  }}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </span>
            ))
          )}
          <ChevronDownIcon className={`ml-auto h-4 w-4 shrink-0 text-heritage-grey ${open ? 'rotate-180' : ''}`} />
        </div>

        {open && (
          <div className="absolute z-50 mt-1.5 w-full overflow-hidden rounded-lg border border-[#E2E5EA] bg-white shadow-lg">
            <div className="border-b border-[#EEF0F3] p-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-heritage-grey" />
                <input
                  ref={searchRef}
                  aria-label="Search people"
                  value={query}
                  placeholder="Search by name or email"
                  onChange={(event) => setQuery(event.target.value)}
                  onClick={(event) => event.stopPropagation()}
                  className="w-full rounded-md border border-[#E2E5EA] py-1.5 pl-8 pr-3 text-[12px] text-blueprint-blue outline-none focus:border-blueprint-blue focus:ring-1 focus:ring-blueprint-blue"
                />
              </div>
            </div>
            <div id={listId} role="listbox" aria-multiselectable="true" className="max-h-60 overflow-y-auto py-1">
              {directoryStatus === 'loading' && (
                <p className="px-3 py-3 text-center text-[12px] text-heritage-grey">Loading people from Cognito…</p>
              )}
              {directoryStatus === 'error' && (
                <p className="px-3 py-3 text-center text-[12px] text-readable-red">
                  The Cognito user list could not be loaded. You can still add an email below.
                </p>
              )}
              {directoryStatus === 'ready' && visible.length === 0 && (
                <p className="px-3 py-3 text-center text-[12px] text-heritage-grey">No people match that search.</p>
              )}
              {directoryStatus !== 'loading' &&
                visible.map((person) => {
                  const selected = emails.includes(person.email)
                  return (
                    <button
                      key={person.email}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left ${
                        selected ? 'bg-[rgba(0,39,63,0.04)]' : 'hover:bg-page-bg'
                      }`}
                      onClick={(event) => {
                        event.stopPropagation()
                        toggle(person.email)
                      }}
                    >
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          selected ? 'border-hero-red bg-hero-red' : 'border-[#CBD0D8] bg-white'
                        }`}
                      >
                        {selected && <CheckIcon className="h-3 w-3 text-white" />}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-[12px] font-medium text-blueprint-blue">
                          {person.name?.trim() || person.email}
                        </span>
                        {person.name?.trim() && (
                          <span className="block truncate text-[11px] text-heritage-grey">{person.email}</span>
                        )}
                      </span>
                    </button>
                  )
                })}
            </div>
            <div className="border-t border-[#EEF0F3]" onClick={(event) => event.stopPropagation()}>
              {adding ? (
                <div className="flex items-center gap-2 p-2">
                  <input
                    ref={addRef}
                    aria-label="Add a person by email"
                    value={draft}
                    disabled={disabled}
                    placeholder="name@crce.com"
                    onChange={(event) => onDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault()
                        addDraft()
                      }
                    }}
                    className="min-w-0 flex-1 rounded-md border border-[#E2E5EA] px-2.5 py-1.5 text-[12px] text-blueprint-blue outline-none focus:border-blueprint-blue focus:ring-1 focus:ring-blueprint-blue"
                  />
                  <button
                    type="button"
                    disabled={disabled || !draft.trim()}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={addDraft}
                    className="rounded-md bg-blueprint-blue px-3 py-1.5 text-[12px] font-medium text-white disabled:opacity-40"
                  >
                    Add
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-hero-red hover:bg-[rgba(235,28,55,0.04)]"
                  onClick={() => setAdding(true)}
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  <span className="text-[12px] font-medium">Add user</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      <p className="text-[11px] text-heritage-grey">
        Search the Cognito user list, tick the people who should see Ask Evans, and use the cross to remove someone.
        Add user is for an address that is not in the list. Save Changes when you are done. If nobody is listed, nobody
        can use Ask Evans.
      </p>
    </div>
  )
}
